<template>
  <vs-card id="renewals-filter">
    <form role="form" @submit.stop.prevent="search">
      <div class="vx-row pb-0 mt-3">
        <div class="vx-col sm:w-1/6 w-full">
          <label class="labelFiltro">Final de vigência entre</label>
          <DatePicker
            class="w-full"
            v-model="filters.endValidityStart"
            format="DD/MM/YYYY"
            placeholder="DD/MM/YYYY"
            value-type="format"
            :editable="true"
            :lang="lang"
            v-mask="'##/##/####'"
            data-testId="renewals-filter-end-validity-start"
          />
        </div>
        <div class="vx-col sm:w-1/6 w-full">
          <label class="labelFiltro">Data Final</label>
          <DatePicker
            class="w-full"
            v-model="filters.endValidityEnd"
            format="DD/MM/YYYY"
            placeholder="DD/MM/YYYY"
            value-type="format"
            :editable="true"
            :lang="lang"
            v-mask="'##/##/####'"
            data-testId="renewals-filter-end-validity-end"
          />
        </div>
        <div class="vx-col sm:w-1/6 w-full">
          <vs-tooltip
            text="Apólices com pontos: insira os 6 últimos números. Apólices sem pontos: insira o número completo."
          >
            <label class="labelFiltro">
              Nº da Apólice
              <vs-icon
                class="icon-spacing"
                color="primary"
                icon-pack="feather"
                icon="icon-help-circle"
              ></vs-icon>
            </label>
            <vs-input
              class="w-full"
              v-model="filters.policyNumber"
              v-mask="'#############'"
              data-testId="renewals-filter-policy-number"
            />
          </vs-tooltip>
        </div>
        <div class="vx-col sm:w-1/6 w-full">
          <label class="labelFiltro">Placa</label>
          <vs-input
            class="w-full"
            v-model="filters.plate"
            v-mask="'AAA-#XX#'"
            style="text-transform: uppercase"
            data-testId="renewals-filter-plate"
          />
        </div>
        <div class="vx-col sm:w-1/6 w-full">
          <label class="labelFiltro">CPF/CNPJ do Segurado</label>
          <vs-input
            class="w-full"
            v-model="filters.taxId"
            v-mask="['###.###.###-##', '##.###.###/####-##']"
            data-testId="renewals-filter-tax-id"
          />
        </div>
        <div class="vx-col sm:w-1/6 w-full">
          <label class="labelFiltro">Status da Apólice</label>
          <v-select
            clearable
            multiple
            v-model="filters.policyStatuses"
            class="w-full"
            :options="policyStatusOptions"
            label="label"
            :reduce="(option) => option.id"
            data-testId="renewals-filter-policy-statuses"
          ></v-select>
        </div>
      </div>
      <div
        v-if="
          canViewBrokersFilter ||
          canViewConsultanciesFilter ||
          canViewCovenantsFilter
        "
        class="vx-row pb-0 mt-3"
      >
        <div v-if="canViewBrokersFilter" class="vx-col sm:w-1/3 w-full">
          <label class="labelFiltro">Corretora</label>
          <v-select
            clearable
            multiple
            v-model="filters.brokers"
            class="w-full"
            :options="brokers"
            label="label"
            :reduce="(option) => option.id"
            data-testId="renewals-filter-brokers"
          >
            <template slot="no-options">{{ noOptionsText }}</template>
          </v-select>
        </div>
        <div v-if="canViewConsultanciesFilter" class="vx-col sm:w-1/3 w-full">
          <label class="labelFiltro">Assessoria</label>
          <v-select
            clearable
            multiple
            v-model="filters.consultancies"
            class="w-full"
            :options="consultancies"
            label="label"
            :reduce="(option) => option.id"
            data-testId="renewals-filter-consultancies"
          >
            <template slot="no-options">{{ noOptionsText }}</template>
          </v-select>
        </div>
        <div v-if="canViewCovenantsFilter" class="vx-col sm:w-1/3 w-full">
          <label class="labelFiltro">Convênio</label>
          <v-select
            clearable
            multiple
            v-model="filters.covenants"
            class="w-full"
            :options="covenants"
            label="label"
            :reduce="(option) => option.id"
            data-testId="renewals-filter-covenants"
          >
            <template slot="no-options">{{ noOptionsText }}</template>
          </v-select>
        </div>
      </div>
    </form>
    <div slot="footer" style="margin-top: -3%">
      <br style="clear: both" />
      <vs-row class="flex justify-end">
        <div class="bg-white mr-3 mb-2 rounded-lg">
          <vs-button
            color="success"
            type="border"
            icon-pack="feather"
            icon="icon-download"
            icon-after
            @click="exportReport"
            data-testId="renewals-filter-export-button"
          >
            Exportar
          </vs-button>
        </div>
        <vs-button
          color="success"
          type="filled"
          icon-pack="feather"
          icon="icon-search"
          class="mr-3 mb-2"
          @click="search"
          data-testId="renewals-filter-search-button"
          >Buscar
        </vs-button>
        <vs-button
          color="danger"
          type="gradient"
          icon-pack="feather"
          icon="icon-trash-2"
          class="mb-2"
          @click="clearFilters"
          data-testId="renewals-filter-clear-button"
        ></vs-button>
      </vs-row>
    </div>
  </vs-card>
</template>
<script>
import vSelect from 'vue-select';
import DatePicker from 'vue2-datepicker';
import ptBr from 'vue2-datepicker/locale/pt-br';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import notify from '../../utils/notify';
import service from '../../services';
import autoApi from '../../services/auto.api';
import { POLICY_STATUSES, SELECT_LEGENDS } from '../../utils/constants';
import {
  PERMISSIONS_ACTIONS,
  PERMISSIONS_NAMES,
} from '../../constants/permissions';

const RENEWALS_MAX_DAYS_RANGE = 30;

const policyStatusOptions = Object.keys(POLICY_STATUSES)
  .map((key) => ({
    id: POLICY_STATUSES[key].id,
    label: POLICY_STATUSES[key].name,
  }))
  .filter((status) => status.id !== POLICY_STATUSES.OPEN.id);

export default {
  name: 'RenewalsFilter',
  components: {
    vSelect,
    DatePicker,
  },
  props: {
    prefill: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      lang: ptBr,
      maxDaysRange: RENEWALS_MAX_DAYS_RANGE,
      policyStatusOptions,
      loading: false,
      user: service.getLoggedUser(),
      brokers: [],
      consultancies: [],
      covenants: [],
      filters: {
        endValidityStart: '',
        endValidityEnd: '',
        policyNumber: '',
        plate: '',
        taxId: '',
        policyStatuses: [],
        brokers: [],
        consultancies: [],
        covenants: [],
      },
    };
  },
  computed: {
    isFullReadingPermission() {
      return this.$can(
        PERMISSIONS_ACTIONS.WALLET_COMPLETE_READ,
        PERMISSIONS_NAMES.RENEWALS,
      );
    },
    canViewCovenantsFilter() {
      return this.isFullReadingPermission;
    },
    canViewConsultanciesFilter() {
      return (
        this.isFullReadingPermission ||
        (this.isCovenantUser && !this.isConsultancyUser)
      );
    },
    canViewBrokersFilter() {
      return (
        this.isFullReadingPermission ||
        this.isCovenantUser ||
        this.isConsultancyUser
      );
    },
    isBrokerUser() {
      return !!(this.user && this.user.id_regional);
    },
    isConsultancyUser() {
      return !!(this.user && this.user.id_divisao);
    },
    isCovenantUser() {
      return !!(this.user && this.user.id_convenio);
    },
    noOptionsText() {
      return this.loading ? SELECT_LEGENDS.LOADING : SELECT_LEGENDS.NO_OPTIONS;
    },
  },
  mounted() {
    this.getContext();
    this.applyProfileScope();
    this.applyPrefill();
  },
  methods: {
    applyProfileScope() {
      if (!this.canViewBrokersFilter && this.isBrokerUser) {
        this.filters.brokers = [this.user.id_regional];
      }
      if (!this.canViewConsultanciesFilter && this.isConsultancyUser) {
        this.filters.consultancies = [this.user.id_divisao];
      }
      if (!this.canViewCovenantsFilter && this.isCovenantUser) {
        this.filters.covenants = [this.user.id_convenio];
      }
    },
    applyPrefill() {
      if (
        !this.prefill ||
        !this.prefill.endValidityStart ||
        !this.prefill.endValidityEnd
      ) {
        return;
      }
      this.filters.endValidityStart = moment(
        this.prefill.endValidityStart,
        'YYYY-MM-DD',
      ).format('DD/MM/YYYY');
      this.filters.endValidityEnd = moment(
        this.prefill.endValidityEnd,
        'YYYY-MM-DD',
      ).format('DD/MM/YYYY');
      if (this.prefill.brokers) {
        const ids = Array.isArray(this.prefill.brokers)
          ? this.prefill.brokers
          : [this.prefill.brokers];
        this.filters.brokers = ids.map((id) => Number(id));
      }
      this.search();
    },
    async getContext() {
      try {
        this.loading = true;
        const full = this.isFullReadingPermission;
        const [brokers, consultancies, covenants] = await Promise.all([
          full ? autoApi.getBrokersList() : autoApi.getAccessibleBrokers(),
          full
            ? autoApi.getConsultanciesList()
            : autoApi.getAccessibleConsultancies(),
          full ? autoApi.getCovenantsList() : autoApi.getAccessibleCovenants(),
        ]);

        this.brokers = brokers || [];
        this.consultancies = consultancies || [];
        this.covenants = covenants || [];
      } catch (error) {
        notify.notifyFail(this.$vs, 'Falha ao carregar filtros de corretora');
      } finally {
        this.loading = false;
      }
    },
    buildPayload() {
      const {
        endValidityStart,
        endValidityEnd,
        policyNumber,
        plate,
        taxId,
        policyStatuses,
        brokers,
        consultancies,
        covenants,
      } = this.filters;

      if (
        (endValidityStart && !endValidityEnd) ||
        (!endValidityStart && endValidityEnd)
      ) {
        notify.notifyFail(
          this.$vs,
          'Informe a data inicial e final do final de vigência',
        );
        return null;
      }

      let start = null;
      let end = null;
      if (endValidityStart && endValidityEnd) {
        start = moment(endValidityStart, 'DD/MM/YYYY');
        end = moment(endValidityEnd, 'DD/MM/YYYY');
        if (end.isBefore(start)) {
          notify.notifyFail(
            this.$vs,
            'A data final deve ser maior ou igual à inicial',
          );
          return null;
        }
        if (end.diff(start, 'days') > this.maxDaysRange) {
          notify.notifyFail(
            this.$vs,
            `O período deve ser de no máximo ${this.maxDaysRange + 1} dias`,
          );
          return null;
        }
      }

      const payload = {};
      if (start && end) {
        payload.endValidityStart = start.format('YYYY-MM-DD');
        payload.endValidityEnd = end.format('YYYY-MM-DD');
      }
      if (policyNumber) payload.policyNumber = Number(policyNumber);
      if (plate) payload.plate = plate;
      if (taxId) payload.taxId = taxId;
      if (policyStatuses.length) payload.policyStatuses = policyStatuses;
      if (brokers.length) payload.brokers = brokers;
      if (consultancies.length) payload.consultancies = consultancies;
      if (covenants.length) payload.covenants = covenants;

      const hasAnyFilter =
        (start && end) ||
        policyNumber ||
        plate ||
        taxId ||
        policyStatuses.length ||
        brokers.length ||
        consultancies.length ||
        covenants.length;

      if (!hasAnyFilter) {
        notify.notifyFail(this.$vs, 'Aplique ao menos um filtro na pesquisa');
        return null;
      }

      return payload;
    },
    search() {
      const payload = this.buildPayload();
      if (payload) this.$emit('search', payload);
    },
    exportReport() {
      const payload = this.buildPayload();
      if (payload) this.$emit('export', payload);
    },
    clearFilters() {
      this.filters = {
        endValidityStart: '',
        endValidityEnd: '',
        policyNumber: '',
        plate: '',
        taxId: '',
        policyStatuses: [],
        brokers: [],
        consultancies: [],
        covenants: [],
      };
      this.applyProfileScope();
      this.$emit('clear');
    },
  },
};
</script>
<style>
#renewals-filter .vs__dropdown-toggle {
  padding: 4px 0 6px 0 !important;
}

#renewals-filter .vs-card--footer {
  margin-top: -3% !important;
}
</style>
